/**
 * Integração com o app iOS da Klinvia (Capacitor, pasta mobile-app/).
 *
 * O app abre este mesmo painel dentro de uma WKWebView. Lá não existem abas,
 * window.print, downloads por <a download> nem pop-ups; tudo isso é desviado
 * para o plugin nativo "KlinviaNative". No navegador, nada aqui tem efeito.
 */

const HANDOFF_PARAM = 'kapp_token';
const LOGOUT_FLAG = 'klinvia_native_logout';
const PLUGIN = 'KlinviaNative';
const JWT_RE = /^[\w-]+\.[\w-]+\.[\w-]+$/;

export type NativeLauncherReason = 'logout' | 'session' | 'switch';

type CapacitorBridge = {
  isNativePlatform?: () => boolean;
  nativePromise?: (plugin: string, method: string, options?: Record<string, unknown>) => Promise<unknown>;
};

function capacitor(): CapacitorBridge | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { Capacitor?: CapacitorBridge }).Capacitor;
}

export function isKlinviaNativeApp(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (!/\bKlinviaApp\//.test(navigator.userAgent || '')) return false;
  try {
    const bridge = capacitor();
    return typeof bridge?.nativePromise === 'function' && bridge.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

function callNative<T = unknown>(method: string, options: Record<string, unknown> = {}): Promise<T> {
  const bridge = capacitor();
  if (!bridge?.nativePromise) return Promise.reject(new Error('Recurso disponível apenas no app.'));
  return bridge.nativePromise(PLUGIN, method, options) as Promise<T>;
}

function reportNativeError(error: unknown): void {
  console.error('[KlinviaNative]', error);
}

// ---------------------------------------------------------------------------
// Sessão: o app faz o login nativo e entrega o token em /#kapp_token=...
// ---------------------------------------------------------------------------

let pendingHandoffToken: string | null = null;

/** Lê e remove o token do fragmento da URL. Chamar antes de renderizar o React. */
export function captureNativeHandoffToken(): void {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash || '';
  if (!hash.includes(`${HANDOFF_PARAM}=`)) return;

  const params = new URLSearchParams(hash.slice(1));
  const token = params.get(HANDOFF_PARAM) || '';
  params.delete(HANDOFF_PARAM);
  const rest = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${window.location.search}${rest ? `#${rest}` : ''}`,
  );

  // Só o app pode entregar sessão por URL: no navegador isso permitiria forçar
  // alguém a entrar numa conta de terceiros com um link.
  if (isKlinviaNativeApp() && JWT_RE.test(token)) pendingHandoffToken = token;
}

export function takeNativeHandoffToken(): string | null {
  const token = pendingHandoffToken;
  pendingHandoffToken = null;
  return token;
}

export function markNativeLogout(): void {
  if (!isKlinviaNativeApp()) return;
  try {
    sessionStorage.setItem(LOGOUT_FLAG, '1');
  } catch {
    /* sessionStorage indisponível */
  }
}

let launcherRequested = false;

/** Volta para a tela de entrada do app. Retorna false fora do app. */
export function openNativeLauncher(reason?: NativeLauncherReason): boolean {
  if (!isKlinviaNativeApp()) return false;
  if (launcherRequested) return true;
  launcherRequested = true;
  let resolved: NativeLauncherReason = reason ?? 'session';
  if (!reason) {
    try {
      if (sessionStorage.getItem(LOGOUT_FLAG)) resolved = 'logout';
      sessionStorage.removeItem(LOGOUT_FLAG);
    } catch {
      /* sessionStorage indisponível */
    }
  }
  callNative('openLauncher', { reason: resolved }).catch((error) => {
    reportNativeError(error);
    launcherRequested = false;
  });
  return true;
}

// ---------------------------------------------------------------------------
// Impressão
// ---------------------------------------------------------------------------

/** Imprime um HTML completo pelo diálogo nativo do iOS. Retorna false fora do app. */
export function nativePrintHtml(html: string, title?: string): boolean {
  if (!isKlinviaNativeApp()) return false;
  callNative('printHtml', { html, title: title || document.title || 'Klinvia', baseUrl: `${window.location.origin}/` }).catch(
    reportNativeError,
  );
  return true;
}

function nativePrintPage(): void {
  callNative('printPage', { title: document.title || 'Klinvia' }).catch(reportNativeError);
}

// ---------------------------------------------------------------------------
// Arquivos e links
// ---------------------------------------------------------------------------

const blobRegistry = new Map<string, Blob>();

const EXTENSION_BY_MIME: Record<string, string> = {
  'application/pdf': 'pdf',
  'text/csv': 'csv',
  'text/plain': 'txt',
  'text/html': 'html',
  'application/json': 'json',
  'application/zip': 'zip',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
};

function fileNameFor(blob: Blob, preferred?: string | null): string {
  const name = (preferred || '').trim();
  if (name) return name;
  const mime = (blob.type || '').split(';')[0].trim().toLowerCase();
  return `arquivo.${EXTENSION_BY_MIME[mime] || 'bin'}`;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function resolveBlob(url: string): Promise<Blob> {
  const known = blobRegistry.get(url);
  if (known) return known;
  const response = await fetch(url);
  return response.blob();
}

/** Abre um arquivo gerado no navegador (blob/data URL ou Blob) no visualizador nativo. */
export async function nativeOpenFile(
  source: Blob | string,
  fileName?: string | null,
  mode: 'preview' | 'share' = 'preview',
): Promise<void> {
  const blob = typeof source === 'string' ? await resolveBlob(source) : source;
  const base64 = await blobToBase64(blob);
  await callNative('shareFile', {
    base64,
    fileName: fileNameFor(blob, fileName),
    mimeType: blob.type || 'application/octet-stream',
    preview: mode === 'preview',
  });
}

function openFileSafely(url: string, fileName?: string | null, mode: 'preview' | 'share' = 'preview'): void {
  nativeOpenFile(url, fileName, mode).catch(reportNativeError);
}

function openExternal(url: string): void {
  callNative('openExternal', { url }).catch(reportNativeError);
}

function parseUrl(raw: string): URL | null {
  try {
    return new URL(raw, window.location.href);
  } catch {
    return null;
  }
}

/** Decide onde abrir um endereço que no navegador iria para outra aba. */
function routeUrl(raw: string, fileName?: string | null): void {
  const url = parseUrl(raw);
  if (!url) return;
  if (url.protocol === 'blob:' || url.protocol === 'data:') {
    openFileSafely(url.href, fileName);
  } else if (url.origin === window.location.origin) {
    window.location.assign(url.href);
  } else if (url.protocol !== 'javascript:') {
    openExternal(url.href);
  }
}

/**
 * Substituto de window.open: as rotinas abrem uma aba vazia antes do `await`
 * e depois escrevem nela ou trocam o endereço. Aqui isso vira impressão,
 * visualizador de arquivo, navegação no próprio painel ou navegador interno.
 */
function createDeferredWindow(initialUrl?: string): Window {
  let html = '';
  let closed = false;
  let navigated = false;
  const go = (target: unknown) => {
    if (closed) return;
    navigated = true;
    routeUrl(String(target));
  };

  const location = {
    get href() {
      return initialUrl || 'about:blank';
    },
    set href(value: string) {
      go(value);
    },
    assign: go,
    replace: go,
    reload: () => undefined,
    toString: () => initialUrl || 'about:blank',
  };

  const doc = {
    title: '',
    open() {
      html = '';
      return doc;
    },
    write(...parts: string[]) {
      html += parts.join('');
    },
    writeln(...parts: string[]) {
      html += `${parts.join('')}\n`;
    },
    close() {},
  };

  const stub = {
    document: doc,
    get location() {
      return location;
    },
    set location(value: unknown) {
      go(value);
    },
    get closed() {
      return closed;
    },
    opener: null as unknown,
    name: '',
    focus() {},
    blur() {},
    postMessage() {},
    addEventListener() {},
    removeEventListener() {},
    print() {
      if (html.trim()) nativePrintHtml(html, doc.title || undefined);
    },
    close() {
      if (closed) return;
      closed = true;
      if (navigated) callNative('closeBrowser').catch(() => undefined);
    },
  };
  return stub as unknown as Window;
}

function nativeWindowOpen(url?: string | URL, _target?: string, _features?: string): Window | null {
  const raw = url == null ? '' : String(url).trim();
  if (!raw || raw === 'about:blank') return createDeferredWindow();
  routeUrl(raw);
  return createDeferredWindow(raw);
}

const OPEN_IN_PLACE_TARGETS = new Set(['', '_self', '_top', '_parent']);

/** Trata um link que o WKWebView não sabe abrir. Retorna true se tratou. */
function handleAnchor(anchor: HTMLAnchorElement): boolean {
  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('#')) return false;
  const url = parseUrl(anchor.href || href);
  if (!url) return false;

  const download = anchor.getAttribute('download');
  const hasDownload = anchor.hasAttribute('download');

  if (url.protocol === 'blob:' || url.protocol === 'data:') {
    openFileSafely(url.href, download, hasDownload ? 'share' : 'preview');
    return true;
  }
  if (!/^https?:$/.test(url.protocol)) return false;

  if (hasDownload) {
    openExternal(url.href);
    return true;
  }
  const target = (anchor.getAttribute('target') || '').toLowerCase();
  if (!OPEN_IN_PLACE_TARGETS.has(target)) {
    routeUrl(url.href);
    return true;
  }
  if (url.origin !== window.location.origin) {
    openExternal(url.href);
    return true;
  }
  return false;
}

let installed = false;

/** Instala os desvios do app. No navegador não faz nada. */
export function installNativeAppBridge(): void {
  if (installed || !isKlinviaNativeApp()) return;
  installed = true;

  document.documentElement.classList.add('klinvia-native-app');

  const originalCreate = URL.createObjectURL.bind(URL);
  const originalRevoke = URL.revokeObjectURL.bind(URL);
  URL.createObjectURL = (obj: Blob | MediaSource) => {
    const url = originalCreate(obj);
    if (obj instanceof Blob) blobRegistry.set(url, obj);
    return url;
  };
  // Muitas rotinas revogam a URL logo após o click(); a leitura do arquivo
  // para o app é assíncrona, então a revogação real fica para depois.
  URL.revokeObjectURL = (url: string) => {
    window.setTimeout(() => {
      blobRegistry.delete(url);
      originalRevoke(url);
    }, 120_000);
  };

  window.open = nativeWindowOpen as typeof window.open;
  window.print = nativePrintPage;

  const originalClick = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function patchedClick(this: HTMLAnchorElement) {
    if (handleAnchor(this)) return;
    originalClick.call(this);
  };

  window.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    const target = event.target as Element | null;
    const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (anchor && handleAnchor(anchor)) event.preventDefault();
  });
}
